import React from 'react';
import SEO from '../components/SEO';

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Digital Accessibility Statement | Equi Pulse Research"
        description="Equipulse Research is committed to ensuring digital accessibility for all users, including persons with disabilities. Read our accessibility statement."
        url="/accessibility"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-8">Digital Accessibility Statement</h1>

        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Commitment to Accessibility</h2>
          <p className="mb-6">
            Equipulse Research is committed to ensuring digital accessibility for all users, including persons with disabilities. We continuously work to improve the accessibility and usability of our website and digital services so that all investors can access our research, insights, and services without barriers. Our goal is to provide an inclusive digital experience in accordance with applicable accessibility standards and regulatory guidelines.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Accessibility Standards Followed</h2>
          <p className="mb-4">
            Our website is designed and maintained in accordance with the following accessibility standards and guidelines:
          </p>
          <ul className="list-disc list-inside mb-8 space-y-2 ml-4">
            <li>Web Content Accessibility Guidelines (WCAG) 2.1 – Level AA</li>
            <li>Rights of Persons with Disabilities (RPwD) Act, 2016</li>
            <li>Guidelines for Indian Government Websites (GIGW)</li>
            <li>IS 17802 Digital Accessibility Standard</li>
          </ul>
          <p className="mb-6">
            These standards help ensure that our digital platforms remain accessible to users with visual, hearing, motor, and cognitive disabilities.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Accessibility Features Implemented</h2>
          <p className="mb-4">
            We have implemented several accessibility features across our website to improve usability for all visitors:
          </p>
          <ul className="list-disc list-inside mb-8 space-y-2 ml-4">
            <li>Structured page headings and semantic HTML for screen reader compatibility</li>
            <li>Alternative text descriptions for informative images and graphics</li>
            <li>Keyboard-accessible navigation for users who cannot use a mouse</li>
            <li>Sufficient color contrast for better readability</li>
            <li>Accessible form labels and instructions</li>
            <li>Responsive layout that supports screen magnification and mobile accessibility</li>
            <li>Clear link descriptions for easier navigation</li>
          </ul>
          <p className="mb-6">
            We continue to monitor and enhance these features as part of our ongoing accessibility improvement process.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Accessible Research Publications</h2>
          <p className="mb-6">
            Equipulse Research strives to make its research publications and digital documents accessible wherever possible. We aim to ensure that investor-facing documents such as research reports and disclosures are structured in a way that improves readability and accessibility for assistive technologies.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Continuous Improvement</h2>
          <p className="mb-6">
            Digital accessibility is an ongoing effort. We periodically conduct accessibility reviews and technical improvements to identify and remove barriers that may affect users with disabilities. We are also working with accessibility specialists and technical tools to further enhance accessibility across all investor-facing digital touchpoints.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Feedback and Assistance</h2>
          <p className="mb-4">
            If you experience difficulty accessing any content on this website, or if you encounter accessibility barriers, we encourage you to contact us. We will make reasonable efforts to provide the information or assistance you require. Please include details about the page or feature you were attempting to access.
          </p>

          <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg mb-8">
            <div className="space-y-2">
              <p><strong>Email:</strong>{' '}
                <a href="mailto:care@equipulseresearch.com" className="text-amber-400 hover:text-amber-300">
                  care@equipulseresearch.com
                </a>
              </p>
              <p><strong>Website:</strong>{' '}
                <a href="https://www.equipulseresearch.com" className="text-amber-400 hover:text-amber-300">
                  www.equipulseresearch.com
                </a>
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Regulatory Context</h2>
          <p className="mb-6">
            Equi Pulse operates as a SEBI-registered Research Analyst (SEBI Reg: INH000024046) and strives to comply with accessibility expectations applicable to investor-facing digital platforms under relevant regulatory guidelines and the Rights of Persons with Disabilities Act, 2016.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Last Updated</h2>
          <p className="mb-6">March 15, 2026</p>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPage;
